<template>
  <div v-if="!revision" class="flex items-center justify-center h-full">
    <ProgressSpinner/>
  </div>
  <div v-if="revision" class="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-7xl mx-auto px-6 py-8">
    <div class="col-span-full">
      <div class="lg:flex lg:flex-row lg:items-center lg:justify-between">
        <h2 class="font-bold text-2xl text-surface-700">
          {{ $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.showing_devices_for_revision_x', {revision: revision.name}) }}
        </h2>
        <div class="flex items-center mt-8 lg:mt-0">
          <Dropdown
              filter
              v-model="filterByFirmware"
              :options="processedFirmwares"
              option-label="label"
              :placeholder="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.filter_devices_by_firmware')"
              @change="filterDevices"
              show-clear
          />
          <IconField icon-position="left" class="ml-2">
            <InputIcon>
              <IconSearch class="text-surface-400 dark:text-surface-600 h-4 w-4"/>
            </InputIcon>
            <InputText
                @change="filterDevices"
                :placeholder="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.search_devices')"
                v-model="deviceFilter"
                @keyup="filterDevices"
            />
          </IconField>
        </div>
      </div>
    </div>

    <Card class="lg:col-span-3">
      <template #title>
        {{
          $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.select_a_device_from_the_list_to_target_a_specific_firmware_version')
        }}
      </template>
      <template #subtitle>
        {{
          $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.use_this_form_to_override_the_manufacturers_approved_firmware_version_with_a_specific_firmware')
        }}
      </template>
      <template #content>
        <PickList
            v-model="devices"
            :show-source-controls="false"
            :show-target-controls="false"
            listStyle="height:342px"
            dataKey="id"
            breakpoint="1024px"
        >
          <template #sourceheader>
            {{ $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.available_devices') }}
            <span>({{  devices[0] ? devices[0].length : 0 }})</span>
          </template>
          <template #targetheader>
            {{ $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.selected_devices') }}
            <span>({{  devices[1] ? devices[1].length : 0 }})</span>
          </template>

          <template #item="{item}">
            <div class="flex flex-wrap items-center gap-4">
              <img class="w-[4rem] shrink-0 rounded-sm"
                   :src="getImageUrl(item)"
                   :alt="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.image_of_the_device_type')"
              />

              <div class="flex-1">
                <h6 class="font-bold text-base text-surface-700 tracking-wide">
                  {{ item.serial_number }}
                </h6>
                <div class="flex items-center gap-2">
                  <div>
                    <span class="text-sm text-surface-500">
                      {{ item.target_firmware?.name ?? approvedFirmware?.name ?? $t('common.na') }}
                    </span>
                    <span class="text-sm text-surface-400"> - </span>
                    <span class="text-sm text-surface-500">
                      {{
                        $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.version_x', {version: item.target_firmware?.version_number ?? approvedFirmware?.version_number ?? $t('common.na')})
                      }}
                    </span>
                    <span class="text-sm text-surface-400" v-if="!item.target_firmware">
                      ({{ $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.default') }})
                    </span>
                  </div>
                </div>
              </div>

              <span v-if="item.asset" class="font-medium text-base text-surface-600 dark:text-surface-0">
                {{ item.asset.name }}
              </span>
            </div>
          </template>
        </PickList>
      </template>
    </Card>

    <div class="grid grid-cols-1 gap-8">
      <div>
        <Card class="col-span-full xl:col-span-1">

          <template #title>
            {{
              $t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.select_a_target_firmware')
            }}
          </template>
          <template #subtitle>
            <span
                v-html="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.please_select_a_firmware_for_the_x_chosen_devices', { count: devices[1]?.length })"></span>
          </template>
          <template #content>
            <FormGroup class="w-full">
              <Dropdown
                  data-cy="select-target-firmware-dropdown"
                  filter
                  v-model="selectedFirmware"
                  :options="processedFirmwares"
                  option-label="label"
                  :placeholder="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.select_a_target_firmware')"
                  show-clear
              />
            </FormGroup>
          </template>

          <template #footer>
            <Button data-cy="track-selected-firmware-button" severity="info" style="display:block;" v-if="$auth.hasPermission('update:devices')"
                    class="mr-2 w-full mb-4"
                    :label="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.track_selected_firmware')"
                    :disabled="!this.selectedFirmware || this.devices[1]?.length === 0"
                    @click="setDevicesToFilteredFirmware"
            />

            <Button style="display:block;" severity="secondary" v-if="$auth.hasPermission('update:devices')"
                    class="mr-2 w-full block"
                    :label="$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.track_default_firmware_x', { firmware: this.approvedFirmware?.version_number })"
                    :disabled="this.devices[1]?.length === 0"
                    @click="setDevicesToDefaultFirmware"
            />
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>

<script>
import ManufacturersSelectDeviceRevision from "@/Pages/Manufacturers/ManufacturersSelectDeviceRevision.vue";
import {mapStores} from "pinia";
import FormGroup from "@/Components/FormGroup.vue";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useDevicesStore} from "@/stores/devices.js";
import {useFirmwaresStore} from "@/stores/firmwares.js";
import {useApprovedFirmwaresStore} from "@/stores/approvedFirmwares.js";
import {IconPlus, IconSearch} from "@tabler/icons-vue";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import {resolveDeviceImageUrl} from "@/Composables/DeviceImage.js";

export default {
  name: "ManufacturersFirmwareUpdateDashboardView",
  components: {IconPlus, IconSearch, ManufacturersSelectDeviceRevision, FormGroup, IconField, InputIcon},
  data() {
    return {
      revision: undefined,
      devices: [],
      deviceFilter: undefined,
      device: {
        default_firmware_id: undefined,
      },
      approvedFirmware: undefined,
      selectedFirmware: undefined,
      filterByFirmware: undefined,
      params: {},
      approvedFirmwareParams: {
        include: 'firmware',
        filter: {
          device_revision_id: null,
          vehicle_manufacturer_id: null,
        }
      },
      firmwareParams: {
        filter: {}
      },
      deviceParams: {
        include: 'currentFirmware,targetFirmware,deviceType,deviceModel,asset',
        filter: {}
      },
    }
  },
  methods: {
    async fetchDeviceForm(deviceId) {
      this.deviceForm = await this.devicesStore.editDevice(deviceId);
    },
    setDevicesToFilteredFirmware() {
      if (this.devices[1]?.length > 0) {
        this.$confirm.require({
          group: 'confirm',
          header: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.override_device_firmware'),
          message: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.confirm_this_will_change_x_devices_target_firmware_to_x_x', {
            count: this.devices[1]?.length,
            firmware: this.selectedFirmware?.name,
            version_number: this.selectedFirmware?.version_number
          }),
          accept: () => {
            this.saveDeviceFirmware(this.selectedFirmware.id);
          },
          reject: () => {
            this.$toast.add({
              severity: 'info',
              summary: this.$t('common.cancelled'),
              detail: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.firmware_was_not_updated'),
              life: 3000
            });
          }
        });
      } else {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.select_devices'),
          detail: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.please_select_the_devices_you_wish_to_update'),
          life: 3000
        });
      }
    },
    setDevicesToDefaultFirmware() {
      if (this.devices[1]?.length > 0) {
        this.$confirm.require({
          group: 'confirm',
          header: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.reset_selected_device_firmware'),
          message: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.confirm_this_will_reset_x_devices_target_firmware_to_x_x', {
            count: this.devices[1]?.length,
            firmware: this.approvedFirmware?.name,
            version_number: this.approvedFirmware?.version_number
          }),
          accept: () => {
            this.saveDeviceFirmware(null);
          },
          reject: () => {
            this.$toast.add({
              severity: 'info',
              summary: this.$t('common.cancelled'),
              detail: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.firmware_was_not_updated'), //'',
              life: 3000
            });
          }
        });
      } else {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.select_devices'),
          detail: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.please_select_the_devices_you_wish_to_update'),
          life: 3000
        });
      }
    },
    async refreshApprovedFirmware() {
      this.approvedFirmwareParams.filter.device_revision_id = this.$route.params.revision;
      this.approvedFirmwareParams.filter.vehicle_manufacturer_id = this.$route.params.manufacturer;

      try {
        await this.approvedFirmwaresStore.indexApprovedFirmwares(this.approvedFirmwareParams);
        let approvedFirmwares = this.approvedFirmwaresStore.getApprovedFirmwares;
        if (approvedFirmwares && approvedFirmwares.length === 1) {
          this.approvedFirmware = approvedFirmwares[0].firmware;
        } else {
          this.approvedFirmware = undefined;
        }
      } catch (e) {
        console.log(e);
      }

    },
    refreshFirmwares(deviceRevisionId) {
      this.firmwareParams.filter.device_revision_ids = [deviceRevisionId];
      return this.firmwaresStore.indexFirmwares(this.firmwareParams);
    },
    refreshDevices() {
      this.deviceParams.filter.device_revision_id = this.$route.params.revision;
      this.deviceParams.filter.vehicle_manufacturer_id = this.$route.params.manufacturer;
      return this.devicesStore.indexDevices(this.deviceParams).then((data) => (this.devices = [data.data, []]))
    },
    getImageUrl(device) {
      return resolveDeviceImageUrl(device, 'https://placehold.co/600x400');
    },

    // TODO: Need to refactor this to bulk update in one request ideally, perhaps via a dedicated route..
    async saveDeviceFirmware(firmwareId) {

      let devicesToUpdate = this.devices[1];

      const promises = devicesToUpdate.map(async deviceToUpdate => {
        return this.fetchDeviceForm(deviceToUpdate.id).then(() => {
          this.deviceForm.target_firmware_id = firmwareId;
          return this.deviceForm.submit();
        })
      });

      Promise.all(promises)
          .then(results => {
            this.refreshDevices();
            this.$toast.add({
              severity: 'success',
              summary: this.$t('common.success'),
              detail: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.updated_firmware'),
              life: 3000
            });
          })
          .catch(error => {
            console.error(error);
            this.$toast.add({
              severity: 'warn',
              summary: this.$t('common.error'),
              detail: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.firmware_was_not_updated'),
              life: 3000
            });
          });
    },
  },
  computed: {
    ...mapStores(useDeviceRevisionsStore),
    ...mapStores(useFirmwaresStore),
    ...mapStores(useApprovedFirmwaresStore),
    ...mapStores(useDevicesStore),
    processedFirmwares() {
      return this.firmwaresStore.getFirmwares?.map(option => ({
        ...option,
        label: this.$t('pages.manufacturers.overrides.manufacturers_firmware_device_overrides_view.firmware_version_x', {
          firmware: option.name ?? this.$t('common.na'),
          version: option.version_number,
        }),
      })) || [];
    },
    filterDevices() {
      let filteredDevices = [];
      let devices = this.devicesStore.getDevices;

      if (devices) {
        // filter by device or asset name
        filteredDevices = devices.filter(item => {
          return (!this.deviceFilter || (item.serial_number.toLowerCase().includes(this.deviceFilter.toLowerCase()) || item.asset?.name.toLowerCase().includes(this.deviceFilter.toLowerCase())));
        })
        // filter by firmware id
        filteredDevices = filteredDevices.filter(item => {
          return (!this.filterByFirmware || (this.filterByFirmware.id == item.target_firmware_id));
        })
        filteredDevices = [filteredDevices, []];
      }
      this.devices = filteredDevices;
      return filteredDevices;
    },
  },
  watch: {
    '$route.params': {
      async handler(newValue, oldValue) {
        if (newValue.revision) {
          try {
            this.revision = await this.deviceRevisionsStore.showDeviceRevision(newValue.revision, this.params);
            await this.refreshFirmwares(this.revision.id);
          } catch (e) {
            this.revision = undefined;
          }
        } else {
          this.revision = undefined;
        }

        this.refreshDevices();
        this.refreshApprovedFirmware();
      },
      immediate: true,
    }
  },
}
</script>

<style scoped>
</style>
